/**
 * Role-aware guided product tour.
 *
 * - Auto-launches on first login (per-user state saved to the backend).
 * - Re-launchable from the "Take the tour" button in the sidebar / portal header
 *   via `window.dispatchEvent(new CustomEvent("ll:tour:show"))`.
 * - Spotlight + popover UX: dark veil with a transparent hole on the target
 *   element, callout card positioned next to it with Prev / Skip / Next.
 * - Steps can hop between pages — `navigate` field triggers a route change
 *   before the step renders.
 * - Variants: owner, accountant, viewer, client. The user's role on the
 *   backend decides which deck plays.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, Sparkles, Rocket, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SPOTLIGHT_PAD = 10;
const LS_PREFIX = "ll_tour_seen_";  // + user.id


/* ---------------- Step decks ---------------- */

const INTRO_STEP = (name) => ({
  selector: null,
  title: `Welcome${name ? `, ${name.split(" ")[0]}` : ""} 👋`,
  body: "Let me walk you through Financials Ready in about 90 seconds. You can skip anytime — the tour will remember where you left off.",
  placement: "center",
  cta: "Start tour",
});

const FINISH_STEP = {
  selector: '[data-testid="sidebar-help-link"]',
  title: "You're all set",
  body: "Everything you just saw is one click away. If you ever want the tour again, hit 'Take the tour' right here in the sidebar.",
  placement: "right",
  cta: "Finish",
  finish: true,
};

// Owner-only finish step with a "preview teammate tours" footer
const OWNER_FINISH_STEP = {
  selector: '[data-testid="sidebar-help-link"]',
  title: "You're all set",
  body: "Everything you just saw is one click away. Curious what your teammates see? Preview any role below — no progress is saved and you can jump back to your own tour anytime.",
  placement: "right",
  cta: "Finish",
  finish: true,
  rolePreview: true,
};

const CLIENT_FINISH_STEP = {
  selector: '[data-testid="client-launch-tour-btn"]',
  title: "You're ready to upload",
  body: "That's it! If you ever want a refresher, tap 'Take the tour' here anytime.",
  placement: "bottom",
  cta: "Finish",
  finish: true,
};

const OWNER_STEPS = (userName) => [
  INTRO_STEP(userName),
  {
    selector: '[data-testid="nav-dashboard"]',
    title: "Dashboard",
    body: "Live P&L, trend charts and AI insights — every number here recomputes from your real transactions in real time.",
    placement: "right",
    navigate: "/app/dashboard",
  },
  {
    selector: '[data-testid="nav-accounts"]',
    title: "Bank Accounts",
    body: "Add every checking, savings or credit card you want to track. Each account needs an opening balance so reconciliation can roll forward.",
    placement: "right",
    navigate: "/app/accounts",
  },
  {
    selector: '[data-testid="nav-statements"]',
    title: "Upload a statement",
    body: "Drop in PDFs, CSVs or Excel files — up to 60 months per account. Claude parses PDFs automatically; CSVs get a column-mapping preview before import.",
    placement: "right",
    navigate: "/app/statements",
  },
  {
    selector: '[data-testid="nav-transactions"]',
    title: "Transactions",
    body: "Every parsed line lives here — filter, bulk-categorize, split transactions across accounts, or mark reconciled. The AI learns from your edits.",
    placement: "right",
    navigate: "/app/transactions",
  },
  {
    selector: '[data-testid="nav-reconciliation"]',
    title: "Reconciliation",
    body: "The Verify panel pairs each txn to its PDF line with keyboard shortcuts (↑/↓, Space, F). Edit opening balances inline, lock closed periods, and bake flags.",
    placement: "right",
    navigate: "/app/reconciliation",
  },
  {
    selector: '[data-testid="nav-reports"]',
    title: "Reports & exports",
    body: "P&L, Balance Sheet, Cash Flow, Expense Report and T-12 — pick a period, compare to prior, export the full close package to PDF or Excel.",
    placement: "right",
    navigate: "/app/reports",
  },
  {
    selector: '[data-testid="nav-reports"]',
    title: "Balance Sheet extras",
    body: "The Balance Sheet lets you add manual assets & liabilities (drag to reorder), book fixed-asset depreciation (SL, DDB or SYD) and show a prior-period column.",
    placement: "right",
  },
  {
    selector: '[data-testid="nav-users"]',
    title: "Team & client portal",
    body: "Invite accountants, viewers, or external clients. Clients land on a sandboxed upload-only portal where they drop statements and chat with your firm.",
    placement: "right",
    navigate: "/app/users",
  },
  {
    selector: '[data-testid="nav-billing"]',
    title: "Billing",
    body: "Upgrade or change plan here. Starter, Pro and Business tiers unlock more bank accounts, team members and features — pay by card via Stripe.",
    placement: "right",
    navigate: "/app/billing",
  },
  OWNER_FINISH_STEP,
];

const ACCOUNTANT_STEPS = (userName) => [
  INTRO_STEP(userName),
  {
    selector: '[data-testid="nav-dashboard"]',
    title: "Dashboard",
    body: "Live P&L, cash-flow trend, and AI insights for the client you're working on.",
    placement: "right",
    navigate: "/app/dashboard",
  },
  {
    selector: '[data-testid="nav-statements"]',
    title: "Upload & parse",
    body: "Drop a PDF, CSV or Excel bank statement — Claude parses PDFs; CSVs preview columns before import. Up to 60 months per account.",
    placement: "right",
    navigate: "/app/statements",
  },
  {
    selector: '[data-testid="nav-transactions"]',
    title: "Transactions",
    body: "Filter, bulk-categorize, split transactions, and mark reconciled. Every manual edit teaches the AI.",
    placement: "right",
    navigate: "/app/transactions",
  },
  {
    selector: '[data-testid="nav-reconciliation"]',
    title: "Reconcile",
    body: "Verify panel pairs each txn to the PDF line. Keyboard shortcuts: ↑/↓ navigate, Space to verify + advance, F to flag.",
    placement: "right",
    navigate: "/app/reconciliation",
  },
  {
    selector: '[data-testid="nav-reports"]',
    title: "Reports & exports",
    body: "P&L, Balance Sheet, Cash Flow, Expenses, T-12 — one-click export to PDF or Excel. The close package bundles all four.",
    placement: "right",
    navigate: "/app/reports",
  },
  FINISH_STEP,
];

const VIEWER_STEPS = (userName) => [
  INTRO_STEP(userName),
  {
    selector: '[data-testid="nav-dashboard"]',
    title: "Dashboard",
    body: "Read-only view of KPIs, monthly trend and AI insights — everything the firm is tracking.",
    placement: "right",
    navigate: "/app/dashboard",
  },
  {
    selector: '[data-testid="nav-transactions"]',
    title: "Transactions",
    body: "Browse every line — filter by category, date, account. You can view and search, but edits are reserved for admins/accountants.",
    placement: "right",
    navigate: "/app/transactions",
  },
  {
    selector: '[data-testid="nav-reports"]',
    title: "Reports",
    body: "P&L, Balance Sheet, Cash Flow and T-12. Export to PDF or Excel whenever you need to share.",
    placement: "right",
    navigate: "/app/reports",
  },
  FINISH_STEP,
];

const CLIENT_STEPS = (userName) => [
  {
    selector: null,
    title: `Welcome${userName ? `, ${userName.split(" ")[0]}` : ""} 👋`,
    body: "Here's a 60-second walkthrough of how to send your bank statements to your accountant. You can skip anytime.",
    placement: "center",
    cta: "Start tour",
  },
  {
    selector: '[data-testid="client-upload-card"]',
    title: "Pick an account",
    body: "First choose which bank account these statements belong to. Your accountant has already set them up for you.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="client-dropzone"]',
    title: "Drop statements here",
    body: "Drag PDFs, CSVs or Excel files right onto this card — or click to browse. Multiple months at once works great.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="client-open-messages-btn"]',
    title: "Chat with your firm",
    body: "Any questions or notes? Tap here to message your accountant directly — attach files, mention transactions, keep it all in one place.",
    placement: "bottom",
  },
  CLIENT_FINISH_STEP,
];

function stepsForVariant(variant, userName) {
  switch (variant) {
    case "accountant": return ACCOUNTANT_STEPS(userName);
    case "viewer":     return VIEWER_STEPS(userName);
    case "client":     return CLIENT_STEPS(userName);
    case "owner":
    default:           return OWNER_STEPS(userName);
  }
}


/* ---------------- Component ---------------- */

export default function ProductTour() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState(null);  // backend tour_state
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  // When owner clicks "Preview Accountant" (or any other role) from the finish
  // step, we temporarily swap the step deck in memory. We never persist a
  // preview — hitting Finish or Skip in preview mode just closes the overlay.
  const [previewVariant, setPreviewVariant] = useState(null);
  const stepRef = useRef(0);

  const effectiveVariant = previewVariant || state?.variant;
  const steps = state ? stepsForVariant(effectiveVariant, user?.name) : [];
  const step = steps[idx];

  // Fetch backend state once per login
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/tour-state");
        if (cancelled) return;
        setState(r.data);
        // Auto-open on first login — only if not completed/dismissed AND the
        // browser hasn't seen the tour this session (prevents re-opening on
        // every full-page refresh after dismissal).
        const seenKey = `${LS_PREFIX}${user.id}`;
        const alreadySeenThisBrowser = localStorage.getItem(seenKey) === "1";
        if (!r.data.completed && !r.data.dismissed && !alreadySeenThisBrowser) {
          setIdx(r.data.last_step_index || 0);
          setTimeout(() => setOpen(true), 900);
        }
      } catch {
        // Non-fatal — tour just won't autostart
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Trigger from external button
  useEffect(() => {
    const onShow = () => {
      setPreviewVariant(null);
      setIdx(0);
      setOpen(true);
    };
    window.addEventListener("ll:tour:show", onShow);
    return () => window.removeEventListener("ll:tour:show", onShow);
  }, []);

  // Save progress on every step change
  const persistProgress = useCallback(async (patch) => {
    try { await api.patch("/tour-state", patch); } catch { /* ignore */ }
  }, []);

  // Compute spotlight rect
  const computeRect = useCallback(() => {
    const current = steps[stepRef.current];
    if (!current?.selector) { setRect(null); return; }
    const el = document.querySelector(current.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - SPOTLIGHT_PAD,
      left: r.left - SPOTLIGHT_PAD,
      width: r.width + SPOTLIGHT_PAD * 2,
      height: r.height + SPOTLIGHT_PAD * 2,
    });
  }, [steps]);

  // Handle route changes when a step has `navigate`. We change route first,
  // then wait a tick for the DOM to update before measuring.
  useEffect(() => {
    stepRef.current = idx;
    if (!open || !step) return;

    if (step.navigate && typeof window !== "undefined") {
      if (window.location.pathname !== step.navigate) {
        navigate(step.navigate);
      }
    }
    // Give routing + render a moment
    const t = setTimeout(computeRect, 180);
    window.addEventListener("resize", computeRect);
    window.addEventListener("scroll", computeRect, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", computeRect);
      window.removeEventListener("scroll", computeRect, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, open]);

  if (!user || !open || !step) return null;

  const isFirst = idx === 0;
  const isLast = idx === steps.length - 1;

  const close = async (finished) => {
    setOpen(false);
    // Preview runs are ephemeral — do not persist state back to the backend.
    if (previewVariant) {
      setPreviewVariant(null);
      return;
    }
    const patch = finished
      ? { completed: true, last_step_index: steps.length - 1 }
      : { dismissed: true, last_step_index: idx };
    const seenKey = `${LS_PREFIX}${user.id}`;
    localStorage.setItem(seenKey, "1");
    await persistProgress(patch);
  };

  const next = async () => {
    if (isLast) { await close(true); return; }
    const n = idx + 1;
    setIdx(n);
    if (!previewVariant) {
      await persistProgress({ last_step_index: n });
    }
  };
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  // Owners can preview any teammate deck from the finish step
  const switchPreview = (variantName) => {
    setPreviewVariant(variantName);
    setIdx(0);
    // Re-anchor on the next tick so the new step's selector gets measured
    setTimeout(() => stepRef.current = 0, 0);
  };

  // Position popover card around spotlight
  let cardStyle = {};
  const CARD_W = 360;
  if (step.placement === "center" || !rect) {
    cardStyle = {
      left: "50%", top: "50%", transform: "translate(-50%, -50%)",
      maxWidth: 460,
    };
  } else if (step.placement === "right") {
    const leftRaw = rect.left + rect.width + 18;
    const left = Math.min(leftRaw, window.innerWidth - CARD_W - 16);
    cardStyle = {
      left: Math.max(16, left),
      top: Math.max(16, Math.min(rect.top - 8, window.innerHeight - 260)),
      maxWidth: CARD_W,
    };
  } else if (step.placement === "bottom") {
    cardStyle = {
      left: Math.max(16, Math.min(rect.left, window.innerWidth - CARD_W - 16)),
      top: Math.min(rect.top + rect.height + 18, window.innerHeight - 240),
      maxWidth: CARD_W,
    };
  } else if (step.placement === "top") {
    cardStyle = {
      left: Math.max(16, Math.min(rect.left, window.innerWidth - CARD_W - 16)),
      top: Math.max(16, rect.top - 220),
      maxWidth: CARD_W,
    };
  }

  // Progress counter — exclude the intro step from "step X of Y" since it's a modal
  const progressTotal = steps.length - 1;  // minus intro
  const progressIdx = Math.max(0, idx);

  return (
    <div className="fixed inset-0 z-[200]" data-testid="product-tour-overlay" aria-modal="true" role="dialog">
      {/* Veil + spotlight */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {rect ? (
          <div
            className="absolute transition-all duration-300 ease-out"
            style={{
              top: rect.top, left: rect.left, width: rect.width, height: rect.height,
              boxShadow: "0 0 0 9999px rgba(8, 20, 40, 0.78)",
              borderRadius: 8,
              outline: "2px solid #FFD166",
              outlineOffset: 3,
            }}
            data-testid="product-tour-spotlight"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "rgba(8, 20, 40, 0.78)" }} />
        )}
      </div>

      {/* Callout card */}
      <div
        className="absolute bg-white border shadow-2xl pointer-events-auto"
        style={{
          ...cardStyle,
          borderColor: "var(--ll-border)",
          animation: "ll-tour-pop 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        data-testid={`product-tour-card-${idx}`}
      >
        <div className="flex items-start gap-3 px-4 pt-4">
          <div
            className="w-8 h-8 grid place-items-center text-white shrink-0"
            style={{ background: "#0b5d9e" }}
          >
            {isLast ? <Rocket size={16} /> : (step.placement === "center" ? <HelpCircle size={16} /> : <Sparkles size={16} />)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] flex items-center gap-2">
              {step.placement === "center" ? "Guided tour" : `Step ${progressIdx} of ${progressTotal}`}
              {previewVariant && (
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-white"
                  style={{ background: "#0b5d9e", letterSpacing: "0.15em" }}
                  data-testid="product-tour-preview-badge"
                >
                  Preview · {previewVariant}
                </span>
              )}
            </div>
            <div className="font-display font-bold text-base mt-0.5">{step.title}</div>
          </div>
          <button
            onClick={() => close(false)}
            data-testid="product-tour-close"
            className="p-1 -m-1 text-[var(--ll-ink-muted)] hover:text-[var(--ll-ink)] shrink-0"
            aria-label="Close tour"
          >
            <X size={14} />
          </button>
        </div>

        <p className="px-4 pt-2 pb-4 text-sm text-black/75 leading-relaxed">{step.body}</p>

        {/* Role-preview chips (owner-only, finish step) */}
        {step.rolePreview && !previewVariant && (
          <div className="px-4 pb-3 flex flex-wrap items-center gap-1.5" data-testid="product-tour-role-preview">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mr-1">Preview as</span>
            {["accountant", "viewer", "client"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => switchPreview(r)}
                data-testid={`product-tour-preview-${r}`}
                className="text-[11px] uppercase tracking-widest px-2 py-1 border hover:bg-[var(--ll-mint)]"
                style={{ borderColor: "var(--ll-border)" }}
              >
                {r}
              </button>
            ))}
          </div>
        )}

        {/* Exit-preview chip */}
        {previewVariant && (
          <div className="px-4 pb-3">
            <button
              type="button"
              onClick={() => { setPreviewVariant(null); setIdx(0); }}
              data-testid="product-tour-exit-preview"
              className="text-[11px] uppercase tracking-widest px-2 py-1 border hover:bg-[var(--ll-mint)] inline-flex items-center gap-1"
              style={{ borderColor: "var(--ll-border)" }}
            >
              <ChevronLeft size={11} /> Back to your tour
            </button>
          </div>
        )}

        {/* Progress dots */}
        <div className="px-4 pb-3 flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className="h-1 transition-all"
              style={{
                width: i === idx ? 20 : 6,
                background: i <= idx ? "#0b5d9e" : "var(--ll-border)",
              }}
            />
          ))}
        </div>

        <div
          className="px-4 py-3 border-t flex items-center justify-between gap-2 bg-[var(--ll-bg-card)]"
          style={{ borderColor: "var(--ll-border)" }}
        >
          {!isFirst && !isLast ? (
            <button
              onClick={prev}
              data-testid="product-tour-prev"
              className="text-xs uppercase tracking-widest px-2.5 py-1.5 hover:bg-white text-[var(--ll-ink-soft)] flex items-center gap-1"
            >
              <ChevronLeft size={12} /> Back
            </button>
          ) : (
            <button
              onClick={() => close(false)}
              data-testid="product-tour-skip"
              className="text-xs uppercase tracking-widest px-2.5 py-1.5 hover:bg-white text-[var(--ll-ink-soft)]"
            >
              Skip
            </button>
          )}
          <button
            onClick={next}
            data-testid="product-tour-next"
            className="text-xs uppercase tracking-widest px-3 py-1.5 bg-[#0b5d9e] hover:opacity-90 text-white flex items-center gap-1"
          >
            {step.cta || (isLast ? "Done" : "Next")} {!isLast && <ChevronRight size={12} />}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes ll-tour-pop {
          from { opacity: 0; transform: ${cardStyle.transform || "translateY(8px)"} scale(0.96); }
          to   { opacity: 1; transform: ${cardStyle.transform || "translateY(0)"} scale(1);     }
        }
      `}</style>
    </div>
  );
}


/* ---------------- Launch button (public helper) ---------------- */

export function TourLaunchButton({ className = "", testid = "launch-tour-btn", label = "Take the tour", icon = true, variant = "ghost" }) {
  const base = "inline-flex items-center justify-center gap-2 text-sm py-2 px-3 transition-colors";
  const styles = variant === "primary"
    ? "bg-[#0b5d9e] text-white hover:opacity-90"
    : "border hover:bg-[var(--ll-mint)]";
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent("ll:tour:show"))}
      data-testid={testid}
      className={`${base} ${styles} ${className}`}
      style={variant === "primary" ? undefined : { borderColor: "var(--ll-border)" }}
    >
      {icon && <HelpCircle size={14} />} {label}
    </button>
  );
}
